import * as React from "react";
import { BaseComponent } from "../../../../react-core/Html/BaseComponent";
import { PartLoading } from "../../../../react-core/Html/PartLoading";
import { JsonDateParser } from "../../../../react-core/Utils/JsonDateParser";
import { ServiceProposition } from "../../../../Wrap/Constants/Enums/ServiceProposition";
import { UserClaim } from "../../../../Wrap/Constants/Enums/UserClaim";
import { AssetUnderManagementModel } from "../../../../Wrap/Models/Models/Business/AssetUnderManagement/AssetUnderManagementModel";
import { CompanySummary } from "../../../../Wrap/Models/Models/Company/CompanySummary";
import { CoachMarkerContext } from "../../../Shared/CoachMarkers/CoachMarkerContext";
import { checkPermission } from "../../../Shared/Permission/PermissionHelper";
import { getQueryParameters } from "../../../Shared/Wizards/Entities/QueryParametersHelper";
import { BusinessManagementAction } from "./Action/BusinessManagementAction";
import { Aum } from "./Component/Aum/Aum";
import { BusinessManagementHeader } from "./Component/BusinessManagementHeader";
import { Charges } from "./Component/Charges";
import { ClientSummary } from "./Component/ClientSummary";
import Search from "./Component/Search/Search.component";
import { WrapperSummary } from "./Component/WrapperSummary";
import { businessManagementCoachmarkerConfig, BusinessManagementTab } from "./Resource/Content";
import { BusinessManagementStore } from "./Store/Variable";

interface IBusinessManagementProps extends React.Props<BusinessManagement> {}

interface IBusinessManagementState {
    data?: AssetUnderManagementModel;
    selectedTab?: BusinessManagementTab;
    firmHierarchyId: string;
    selectedFirm: CompanySummary;
    multiPropsFilter: ServiceProposition[];
    selectedProposition: ServiceProposition[];
    code: string;
}

export class BusinessManagement extends BaseComponent<IBusinessManagementProps, IBusinessManagementState> {
    constructor(props: any) {
        super(props);
        this.state = {
            data: null,
            selectedTab: BusinessManagementTab.AUM,
            firmHierarchyId: getQueryParameters()["firm"],
            code: getQueryParameters()["code"],
            multiPropsFilter: [],
            selectedFirm: null,
            selectedProposition: [],
        };

        this.subscribeToStore(BusinessManagementStore, () => this.onBusinessManagementStoreChange());
    }

    public componentDidMount() {
        if (this.state.firmHierarchyId) {
            this.getCompanyProposition(this.state.firmHierarchyId);
            const { setPageConfig } = this.context;
            // Setup Coachmarks for this page
            //setPageConfig(businessManagementCoachmarkerConfig);
        }
    }

    public componentWillUnmount() {
        const { setPageConfig } = this.context;
        setPageConfig(null);
    }

    public render(): JSX.Element {
        const { data, firmHierarchyId, code, selectedProposition } = this.state;
        if (!checkPermission(UserClaim.AdviserPortalBusinessManagementReadWrite)) {
            return null;
        }

        if (!firmHierarchyId || !code) {
            return (
                <div role="main" id="main">
                    <h1>Select the firm you'd like to view</h1>
                    <hr />
                    <Search
                        onChangeFirmHierarchyId={(firm) => {
                            if (!firm) {
                                return;
                            }
                            this.onSelectFirm(firm);
                        }}
                    />
                </div>
            );
        } else if (selectedProposition.length == 0) {
            return <PartLoading isLoading />;
        } else {
            return (
                <div role="main" id="main">
                    <BusinessManagementHeader
                        data={data}
                        multiPropsFilter={this.state.multiPropsFilter}
                        onMultiPropsFilterChange={(v) => this.setState({ selectedProposition: v })}
                    />
                    {this.getTabs()}
                    <div className="anim-navigation-wrapper">{this.getContent()}</div>
                </div>
            );
        }
    }

    private onBusinessManagementStoreChange() {
        const data = BusinessManagementStore.getData();
        this.setState({
            data,
        });
    }

    private getPastYear(toDate: Date): Date {
        return this.isLeapDay(toDate)
            ? new Date(toDate.getFullYear() - 1, 2, 1)
            : new Date(toDate.getFullYear() - 1, toDate.getMonth(), toDate.getDate() + 1);
    }

    private onSelectFirm(firm: CompanySummary) {
        window.location.assign(window.location.href + `?firm=${firm?.HierarchyId}&code=${firm?.CompanyCode}`);
    }

    private getCompanyProposition(hierarchyId) {
        BusinessManagementAction.getCompanyServiceProposition(hierarchyId, (val: Array<ServiceProposition>) => {
            this.setState({
                multiPropsFilter: val,
                selectedProposition: val,
            });
        });
    }

    private isLeapDay(toDate: Date) {
        const year = toDate.getFullYear();
        const isLeapYear = year % 100 === 0 ? year % 400 === 0 : year % 4 === 0;
        const leapdaystr = toDate.getFullYear() + "/02/29";
        const todaystr = JsonDateParser.getFormatDateString(toDate, "yyyy/MM/dd");
        return isLeapYear && todaystr === leapdaystr;
    }

    private getContent(): JSX.Element {
        switch (this.state.selectedTab) {
            case BusinessManagementTab.AUM:
                return <Aum selectedProposition={this.state.selectedProposition} />;
            case BusinessManagementTab.WrapperSummary:
                return <WrapperSummary selectedProposition={this.state.selectedProposition} />;
            case BusinessManagementTab.Charges:
                return <Charges selectedProposition={this.state.selectedProposition} />;
            case BusinessManagementTab.ClientSummary:
                return <ClientSummary selectedProposition={this.state.selectedProposition} />;
        }
    }

    private getTabs(): JSX.Element {
        const availableTabs = [
            BusinessManagementTab.AUM,
            BusinessManagementTab.WrapperSummary,
            BusinessManagementTab.Charges,
            BusinessManagementTab.ClientSummary,
        ];

        const tabs = [];

        for (const key of availableTabs) {
            tabs.push(
                <button
                    role="tab"
                    key={`tab-${key}`}
                    className="item"
                    aria-selected={this.state.selectedTab == key}
                    aria-controls={`panel-${key}`}
                    id={`tab-${key}`}
                    onClick={() => this.setState({ selectedTab: key })}
                >
                    {key}
                </button>
            );
        }

        return (
            <div role="tablist" aria-label="Business management tabs" className="tabs">
                {tabs}
            </div>
        );
    }
}
BusinessManagement.contextType = CoachMarkerContext;
